/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

// Filter functions interface - AngularJS filter equivalents
export interface FilterService {
  // Internationalization
  translate: (key: string, params?: any) => string

  // Text Processing
  excerpt: (text: string, length?: number) => string
  capitalize: (text: string) => string
  trusted: (html: string) => string

  // File and Data Processing
  extension: (filename: string) => string
  mimetype: (filename: string) => string
  bytes: (bytes: number) => string

  // Formatting
  date: (date: Date | string, format?: string) => string
  number: (num: number, format?: string) => string
}

// Filter service implementation - organized by function type
export const createFilterService = (t: (key: string, params?: any) => string): FilterService => ({
  // Internationalization
  translate: (key: string, params?: any) => {
    const result = t(key, params)
    return typeof result === 'string' ? result : String(result)
  },

  // Text Processing
  excerpt: (text: string, length = 100) => {
    return text.length > length ? text.substring(0, length) + '...' : text
  },
  capitalize: (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
  },
  trusted: (html: string) => html, // TODO: Implement sanitization

  // File and Data Processing
  extension: (filename: string) => {
    const ext = filename.split('.').pop()
    return ext || ''
  },
  mimetype: (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase()
    const mimeTypes: Record<string, string> = {
      // Document types
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      txt: 'text/plain',

      // Spreadsheet types
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

      // Image types
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif'
    }
    return mimeTypes[ext || ''] || 'application/octet-stream'
  },
  bytes: (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    if (bytes === 0) return '0 Bytes'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i]
  },

  // Formatting
  date: (date: Date | string, format?: string) => {
    const d = new Date(date)
    return d.toLocaleDateString()
  },
  number: (num: number, format?: string) => {
    return num.toLocaleString()
  }
})

export const formatName = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase()
  const formatNames: Record<string, string> = {
    // Document types
    pdf: 'PDF',
    doc: 'Word Document',
    docx: 'Word Document',
    txt: 'Text',

    // Spreadsheet types
    xls: 'Excel Spreadsheet',
    xlsx: 'Excel Spreadsheet',
    csv: 'Comma Separated Values',

    // Presentation types
    ppt: 'PowerPoint',
    pptx: 'PowerPoint',

    // Image types
    jpg: 'JPG/JPEG Image',
    jpeg: 'JPG/JPEG Image',
    png: 'PNG Image',
    gif: 'Images',
    bmp: 'Images',
    tiff: 'Images',
    ico: 'Images',
    psd: 'Adobe Photoshop',
    ai: 'Adobe Illustrator',
    eps: 'Encapsulated PostScript'
  }
  return formatNames[ext || ''] || '–'
}
